import { SkinnedOwnerStageElement } from "./skinned-owner-stage.js";
import { escapeHtml } from "../util/escape-html.js";
const QUERY = "peers.directory.v1";
export const FRIENDS_STAGE_TAG = "node-friends-stage";
export const FRIENDS_QUERY = QUERY;
/**
 * Owner stage: friends — re-clothed to the original skin's messenger layout (epic #1507
 * fidelity, stage 5/5, LAST stage). Ported from the vendored `index.html:205-311` rail markup
 * and `SKIN/stages/default/friends/view/view.js`'s chat pane, verified real via explicit-file
 * grep against `client/ui/src/skin/stages-css/friends.css` (rail-hd/rail-tools/search/new-btn/
 * rf-section-hd/rf-msg-list/chat-row/c-ava/ava-1..8/c-name/c-time/c-preview/fr-no-results/
 * msg-hd/mava/mhd-id/mstatus/ws/m-tools/m-icon/m-ai/m-divider/m-more/msg-stream/msg-composer/
 * mc-input/mc-send all present there) plus `client/ui/src/skin/css/legacy-numbered.css` (the
 * `.fr-digest*` rules — NOT in `friends.css` itself, but loaded globally via
 * `skin-stylesheet.ts`, so they still apply to this stage's placeholder card).
 *
 * Peers are real (`peers.directory.v1`, `OwnerViewService.listPeers`, confirmed via
 * `grep -rn "peers.directory.v1" client/`); there is no kernel query for message content, so
 * the chat/composer pane is styled PLACEHOLDER chrome, never a fabricated thread — wiring real
 * messages is `#1539`'s job, tracked below.
 *
 * RAIL (`renderRail`, mounted into the shell's real `[data-rail]` — see
 * `SkinnedOwnerStageElement#renderRail`):
 * - `.rail-hd > .rail-tools`: the original's `.search` (svg + input, disabled — no live filter
 *   wired) + `.new-btn` (disabled — no create-chat capability). The original's `.rf-story-row`
 *   (5 fabricated named contacts + an "Add" affordance) is skipped entirely: it's
 *   `display:none !important` in the vendored `friends.css` — confirmed dead chrome, not a
 *   trimmed feature.
 * - `.rail-body#chatList`: `.rf-section-hd` ("Peers" + a real `N` count in `.rf-sh-meta`, vs.
 *   the original's fabricated "3 unread") + `.rf-msg-list` of real `.chat-row`s, each with a
 *   rotating `.c-ava.ava-N` (index mod 8, matching the original's per-row color cycling — the
 *   old markup rendered every avatar with the same untinted `.c-ava`), `.c-name`, `.c-time`
 *   (from `lastSeenAt`), and `.c-preview` (peer `status`, replacing the original's fabricated
 *   per-peer message previews like "Lol that's wild" / "shared a link"). A static
 *   `.fr-no-results` sits at the end of `#chatList`, matching the original's DOM position;
 *   the vendored CSS defaults it to `display:none` (a `.rail-body`-level state class the
 *   original's search JS would toggle on zero matches — not wired here, so it stays hidden,
 *   which is correct: there's always at least one real row when `renderRail` runs).
 *
 * MAIN (`renderView`, this stage's own `.main-view[data-view="friends"]` subtree):
 * - `.msg-hd`: `.fr-back` (disabled — no mobile drawer to close from here), `.mava` reading
 *   "—" (no chat is open, so there's no real peer initials/DID to show; the CSS's
 *   `.mava::after` presence dot is unconditional decorative chrome inherited from the
 *   original, the same way `wallet-stage`'s `.sys-live .ld` dot and `agents-stage`'s
 *   `.cg-ab-live` dot stay lit for placeholder cards — it isn't a claim about a specific
 *   peer's online state), `.mhd-id` ("Select a chat" / "No conversation open" + a blank `.ws`
 *   chip), and the original's `.m-tools` icon row ported verbatim — `.m-icon.m-ai`, search,
 *   a `.m-divider`, audio + video call, another `.m-divider`, `.m-more` — all `disabled`
 *   (pure chrome; no assistant/search/call/menu capability is wired to a chat that isn't
 *   open).
 * - `.msg-stream#msgStream`: a single `.fr-digest`-styled card reusing the original's
 *   distinctive AI-summary look, but with an honest label/body instead of the original's
 *   fabricated "Caught up · 8 messages" / dinner-plan digest text. No `.fr-float-card`s,
 *   `.bubble-msg` thread, `.fr-smart` chips, or `.typing-bubble` are ported — all fabricated
 *   demo content the data policy forbids.
 * - `.msg-composer > .mc-input`: `textarea` ("Text message", disabled) + `.mc-send`
 *   (disabled) — no send capability wired to an unopened, unwired chat.
 *
 * #1539 (message data wiring) will need to: open a real conversation from a `.chat-row` click,
 * populate `.mhd-id`/`.mava` from the selected peer, replace the `.fr-digest` placeholder with
 * a real (or removed) AI digest, and stream real `.bubble-msg` turns into `#msgStream`, then
 * enable the composer.
 */
export class FriendsStageElement extends SkinnedOwnerStageElement {
    query = QUERY;
    emptyMessage = "No peers synced yet.";
    renderView(root, _rows) {
        root.innerHTML = `
      <div class="msg-hd">
        <button class="fr-back" type="button" aria-label="Back to chats" disabled>
          <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M8.5 3 L4.5 7 L8.5 11"/></svg>
        </button>
        <div class="mava">&mdash;</div>
        <div class="mhd-id">
          <div class="mname">Select a chat</div>
          <div class="mstatus">
            <span>No conversation open</span>
            <span class="ws">&mdash;</span>
          </div>
        </div>
        <div class="m-tools">
          <button class="m-icon m-ai" type="button" title="Not available yet" disabled>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2.2 L9.3 6 L13 7.3 L9.3 8.6 L8 12.4 L6.7 8.6 L3 7.3 L6.7 6 Z"/></svg>
          </button>
          <button class="m-icon" type="button" title="Not available yet" disabled>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><circle cx="7" cy="7" r="4.4"/><path d="M10.3 10.3 L13.6 13.6"/></svg>
          </button>
          <div class="m-divider"></div>
          <button class="m-icon" type="button" title="Not available yet" disabled>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3.4 Q3 2.6 3.8 2.6 L5.4 2.6 Q6 2.6 6.2 3.2 L6.9 5.2 Q7.1 5.8 6.6 6.2 L5.7 6.9 Q6.7 9 9.1 10.3 L9.8 9.4 Q10.2 8.9 10.8 9.1 L12.8 9.8 Q13.4 10 13.4 10.6 L13.4 12.2 Q13.4 13 12.6 13 Q3 12.4 3 3.4 Z"/></svg>
          </button>
          <button class="m-icon" type="button" title="Not available yet" disabled>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="4.5" width="9" height="7" rx="2"/><path d="M11 7.2 L14 5.4 V10.6 L11 8.8 Z"/></svg>
          </button>
          <div class="m-divider"></div>
          <button class="m-more" type="button" title="Not available yet" disabled>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M4 8 H4.01 M8 8 H8.01 M12 8 H12.01"/></svg>
          </button>
        </div>
      </div>
      <div class="msg-stream" id="msgStream">
        <div class="fr-digest" data-friends-placeholder>
          <div class="fr-digest-head">
            <span class="fr-digest-mark">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2.2 L9.3 6 L13 7.3 L9.3 8.6 L8 12.4 L6.7 8.6 L3 7.3 L6.7 6 Z"/></svg>
            </span>
            <span class="fr-digest-label">No conversation open</span>
          </div>
          <span class="fr-digest-body">Messages will appear here once chat is wired to live data.</span>
        </div>
      </div>
      <div class="msg-composer">
        <div class="mc-input">
          <textarea placeholder="Text message" rows="1" disabled></textarea>
          <button class="mc-send" type="button" title="Send" disabled>
            <svg viewBox="0 0 17 17" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M8.5 14 L8.5 3 M4 7.5 L8.5 3 L13 7.5"/></svg>
          </button>
        </div>
      </div>
    `;
    }
    renderRail(root, rows) {
        const list = rows.map((row, index) => renderChatRow(row, index)).join("");
        root.innerHTML = `
      <div class="rail-view" data-view="friends" data-friends-peer-list>
        <div class="rail-hd">
          <div class="rail-tools">
            <div class="search">
              <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><circle cx="6" cy="6" r="4"/><path d="M9 9 L12 12"/></svg>
              <input type="text" placeholder="Search chats&hellip;" disabled />
            </div>
            <button class="new-btn" type="button" title="New chat" disabled>
              <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M7 3 V11 M3 7 H11"/></svg>
            </button>
          </div>
        </div>
        <div class="rail-body" id="chatList">
          <div class="rf-section-hd">
            <span class="rf-sh-title">Peers</span>
            <span class="rf-sh-meta">${rows.length}</span>
          </div>
          <div class="rf-msg-list">${list}</div>
          <div class="fr-no-results">No conversations found</div>
        </div>
      </div>
    `;
    }
}
export function registerFriendsStageElement() {
    if (!customElements.get(FRIENDS_STAGE_TAG)) {
        customElements.define(FRIENDS_STAGE_TAG, FriendsStageElement);
    }
}
function renderChatRow(row, index) {
    const displayName = stringField(row.displayName) ?? "Unknown peer";
    const id = stringField(row.id) ?? "";
    const status = stringField(row.status) ?? "unknown";
    const lastSeenAt = stringField(row.lastSeenAt);
    const online = status === "online";
    const avaClass = `ava-${(index % 8) + 1}`;
    return `
            <div class="chat-row" data-chat="${escapeHtml(id)}">
              <div class="c-ava ${avaClass}${online ? " online" : ""}">${escapeHtml(initialsFor(displayName))}</div>
              <div class="c-name">${escapeHtml(displayName)}</div>
              <div class="c-time">${escapeHtml(formatLastSeen(lastSeenAt))}</div>
              <div class="c-preview">${escapeHtml(status)}</div>
            </div>`;
}
function stringField(value) {
    return typeof value === "string" && value.length > 0 ? value : undefined;
}
function initialsFor(displayName) {
    const parts = displayName.trim().split(/\s+/u).filter(Boolean);
    const initials = parts.slice(0, 2).map((part) => part[0]?.toUpperCase() ?? "").join("");
    return initials.length > 0 ? initials : "?";
}
function formatLastSeen(value) {
    if (value === undefined)
        return "";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime()))
        return "";
    return parsed.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}
//# sourceMappingURL=friends-stage.js.map